import {
  type ApiBlock,
  type TextractDocument,
} from "amazon-textract-response-parser";
import { useCallback, useEffect, useRef } from "react";

interface ICanvasComponentProps {
  src: string;
  textractData: TextractDocument;
  onSelect: ({ text, line }: { text: string; line: CustomBlock }) => void;
  setCanvasLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface CustomBlock {
  left: number;
  top: number;
  width: number;
  height: number;
}

type CustomApiBlock = ApiBlock & {
  Text: string;
};

export default function CanvasComponent({
  src,
  textractData,
  onSelect,
  setCanvasLoading
}: ICanvasComponentProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());
  const isDrawingRef = useRef<boolean>(false);
  const isDrawingInProgressRef = useRef<boolean>(false);
  const startPosRef = useRef<Record<string, number>>({ x: 0, y: 0 });

  const extractText = useCallback(
    (line: CustomBlock) => {
      const isBlockWithin = (
        block: ApiBlock,
        left: number,
        top: number,
        width: number,
        height: number
      ) => {
        if (!block.Geometry) return;
        const blockLeft = block?.Geometry?.BoundingBox.Left;
        const blockTop = block?.Geometry?.BoundingBox.Top;
        const blockWidth = block?.Geometry?.BoundingBox.Width;
        const blockHeight = block?.Geometry?.BoundingBox.Height;

        const right = left + width;
        const bottom = top + height;
        const blockRight = blockLeft + blockWidth;
        const blockBottom = blockTop + blockHeight;

        return (
          blockLeft < right &&
          blockRight > left &&
          blockTop < bottom &&
          blockBottom > top
        );
      };

      const getTextFromBlocks = (
        left: number,
        top: number,
        width: number,
        height: number
      ) => {
        const data = textractData
          .listBlocks()
          .filter(
            (block) =>
              block.BlockType === "WORD" &&
              isBlockWithin(block, left, top, width, height)
          )
          .filter((block) => (block as CustomApiBlock).Text.trim() !== "")
          .map((block) => (block as CustomApiBlock).Text)
          .join(" ");
        return data.trim();
      };

      if (textractData?.listBlocks().length > 0) {
        const text = getTextFromBlocks(
          line.left,
          line.top,
          line.width,
          line.height
        ).trim();
        return { text, line };
      }
      return { text: "", line };
    },
    [textractData]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      const image = imageRef.current;
      const rect = canvas?.getBoundingClientRect();
      if (rect == null) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (canvas != null && context != null) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const customLines = textractData
          .listBlocks()
          .filter((block) => block.BlockType === "LINE")
          .map((line) => {
            if (line.Geometry?.BoundingBox == null) return null;
            const { Left, Top, Width, Height } = line.Geometry.BoundingBox;
            const left = Left * canvas.width;
            const top = Top * canvas.height;
            const width = Width * canvas.width;
            const height = Height * canvas.height;

            return {
              left,
              top,
              width,
              height,
            } satisfies CustomBlock;
          })
          .filter((item) => item != null);
        customLines.some((line) => {
          const { left, top, width, height } = line as CustomBlock;
          context.beginPath();
          context.rect(left, top, width, height);
          if (context.isPointInPath(x, y)) {
            context.strokeStyle = "red";
            context.lineWidth = 2;
            context.strokeRect(left, top, width, height);
            context.closePath();
            return true;
          }
          context.closePath();
          return false;
        });
      }

      if (isDrawingRef.current && canvas != null && context != null) {
        const { x: startX, y: startY } = startPosRef.current;
        const rectWidth = x - startX;
        const rectHeight = y - startY;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.strokeStyle = "red";
        context.lineWidth = 1;
        context.strokeRect(startX, startY, rectWidth, rectHeight);
      }
    },
    [textractData]
  );

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (rect == null) return;
    const x = e.clientX - rect?.left;
    const y = e.clientY - rect?.top;
    startPosRef.current = { x, y };
    isDrawingRef.current = true;
    isDrawingInProgressRef.current = true;
  }, []);

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      const image = imageRef.current;
      if (isDrawingRef.current && canvas != null && context != null) {
        isDrawingRef.current = false;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const { x: startX, y: startY } = startPosRef.current;
        const rectWidth = x - startX;
        const rectHeight = y - startY;
        if (rectWidth > 0 && rectHeight > 0) {
          onSelect(
            extractText({
              left: startX / canvas.width,
              top: startY / canvas.height,
              width: rectWidth / canvas.width,
              height: rectHeight / canvas.height,
            } satisfies CustomBlock)
          );
        }
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
    },
    [extractText, onSelect]
  );

  const handleMouseClick = useCallback(
    (e: MouseEvent) => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (canvas != null && context != null) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const customLines = textractData
          .listBlocks()
          .filter((block) => block.BlockType === "LINE")
          .map((line) => {
            if (line.Geometry?.BoundingBox == null) return null;
            const { Left, Top, Width, Height } = line.Geometry.BoundingBox;
            const left = Left * canvas.width;
            const top = Top * canvas.height;
            const width = Width * canvas.width;
            const height = Height * canvas.height;

            return {
              left,
              top,
              width,
              height,
            } satisfies CustomBlock;
          })
          .filter((item) => item != null);
        customLines.some((line) => {
          const { left, top, width, height } = line as CustomBlock;
          context.beginPath();
          context.rect(left, top, width, height);
          if (context.isPointInPath(x, y)) {
            const normalizedLeft = left / canvas.width;
            const normalizedTop = top / canvas.height;
            const normalizedWidth = width / canvas.width;
            const normalizedHeight = height / canvas.height;
            onSelect(
              extractText({
                left: normalizedLeft,
                top: normalizedTop,
                width: normalizedWidth,
                height: normalizedHeight,
              } satisfies CustomBlock)
            );
            context.closePath();
            return true;
          }
          context.closePath();
          return false;
        });
      }
    },
    [textractData, extractText, onSelect]
  );

  useEffect(() => {
    if (src != null && textractData != null) {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      const image = imageRef.current;

      const drawImageAndBlocks = () => {
        if (canvas != null && context != null) {
          const parentWidth = canvas?.parentElement?.offsetWidth;
          const scale = parentWidth! / image.width;
          const scaledHeight = image.height * scale;
          canvas.height = scaledHeight;
          canvas.width = Math.min(parentWidth!, image.width);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          if (setCanvasLoading) {
            setCanvasLoading(false);
          }
        }
      };

      image.src = src;
      image.onload = drawImageAndBlocks;

      canvas?.addEventListener("click", handleMouseClick);
      canvas?.addEventListener("mousemove", handleMouseMove);
      canvas?.addEventListener("mousedown", handleMouseDown);
      canvas?.addEventListener("mouseup", handleMouseUp);
      return () => {
        canvas?.removeEventListener("click", handleMouseClick);
        canvas?.removeEventListener("mousemove", handleMouseMove);
        canvas?.removeEventListener("mousedown", handleMouseDown);
        canvas?.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    src,
    textractData,
    onSelect,
    extractText,
    handleMouseClick,
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
  ]);

  return <canvas ref={canvasRef} className="block m-auto" />;
}
